<template>
  <div class="document-omt-tab-container">
    <div class="position-absolute d-flex flex-column w-100 h-100">
      <div
        v-for="result in groupedResults"
        :key="result.id"
        class="form-result"
        style="width: calc(100% - 8px);"
      >
        <h4 class="form-name">{{ result.form.name }}</h4>
        <span v-if="result.form.subtitle" class="form-subtitle">
          {{ result.form.subtitle }}
        </span>
        <div v-if="result.allNumbers">
          <div class="line-chart-wrapper">
            <chart
              :data="result.lineChart.data"
              :options="result.lineChart.options"
              type="shadowLine"
              class="line-chart"
            />
          </div>
          <div class="bar-chart-wrapper">
            <chart
              :data="result.barChart.data"
              :options="result.barChart.options"
              :plugins="plugins"
              :style="{ height: result.barChart.height }"
              type="bar"
            />
          </div>
        </div>
        <div v-else class="pt-4 pb-2">
          <b-table
            class="omt-results-table"
            striped
            hover
            :items="result.scores"
            borderless
            small
          >
            <template #cell(date)="data">
              {{ formatDateOnly(data.item.date) }}
            </template>
            <template #table-colgroup="scope">
              <col
                v-for="field in scope.fields"
                :key="field.key"
                :style="{ width: field.key === 'date' ? '120px' : 'initial' }"
              />
            </template>
          </b-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ChartDataLabels from "chartjs-plugin-datalabels";
import Chart from "../../controls/Chart.vue";
import { groupBy } from "../../../helpers/general";

export default {
  name: "DocumentOmtTabContent",
  components: { Chart },
  props: {
    results: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      plugins: [ChartDataLabels],
      barChart: {}
    };
  },
  methods: {
    barBackgroundGradient(context, id) {
      const chart = context.chart;
      const { ctx, chartArea } = chart;

      if (!chartArea) {
        // This case happens on initial chart load
        return;
      }

      if (!this.barChart[id]) {
        this.$set(this.barChart, id, {
          width: undefined,
          height: undefined,
          gradient: undefined
        });
      }

      const chartWidth = chartArea.right - chartArea.left;
      const chartHeight = chartArea.bottom - chartArea.top;
      if (
        !this.barChart[id].gradient ||
        this.barChart[id].width !== chartWidth ||
        this.barChart[id].height !== chartHeight
      ) {
        // Create the gradient because this is either the first render
        // or the size of the chart has changed
        this.barChart[id].width = chartWidth;
        this.barChart[id].height = chartHeight;
        this.barChart[id].gradient = ctx.createLinearGradient(
          chartArea.left,
          chartArea.top,
          chartArea.right,
          chartArea.bottom
        );
        this.barChart[id].gradient.addColorStop(0, "#0285FC");
        this.barChart[id].gradient.addColorStop(1, "#913dff");
      }

      return this.barChart[id].gradient;
    }
  },
  computed: {
    groupedResults() {
      return Array.from(groupBy(this.results, d => d.formId).values()).map(
        g => {
          const formId = g[0].formId;
          const allNumbers = g.filter(d => isNaN(d.score)).length == 0;
          const chartData = g.map(d => d.score ?? 0);
          const scores = g.map(d => ({
            date: d.effectiveDate,
            value: d.score
          }));
          const maxValue = Math.max(...chartData);
          return {
            form: g[0].form,
            allNumbers,
            scores,
            lineChart: {
              data: {
                labels: g.map(() => ""),
                datasets: [
                  {
                    label: "Score",
                    borderColor: "#2196F3",
                    pointBackgroundColor: "rgba(21, 150, 243, 1)",
                    backgroundColor: "transparent",
                    data: chartData,
                    cubicInterpolationMode: "monotone"
                  }
                ]
              },
              options: {
                maintainAspectRatio: false,
                elements: { point: { radius: 0 } },
                layout: {
                  padding: { top: -20, right: 10, bottom: -20, left: 10 }
                },
                scales: {
                  y: {
                    grace: 2,
                    offset: true,
                    ticks: { display: false },
                    grid: {
                      drawOnChartArea: false,
                      drawBorder: false,
                      display: false
                    }
                  },
                  x: {
                    offset: true,
                    grid: {
                      drawOnChartArea: false,
                      drawBorder: false,
                      display: false
                    }
                  }
                },
                plugins: {
                  legend: { display: false },
                  tooltip: { enabled: false }
                }
              }
            },
            barChart: {
              height: `${20 + chartData.length * 30}px`,
              data: {
                labels: g.map(d => this.formatDateOnly(d.effectiveDate)),
                datasets: [
                  {
                    label: "Score",
                    backgroundColor: context => {
                      return this.barBackgroundGradient(context, formId);
                    },
                    data: chartData,
                    cubicInterpolationMode: "monotone",
                    barThickness: 16
                  }
                ]
              },
              options: {
                indexAxis: "y",
                maintainAspectRatio: false,
                scales: {
                  y: {
                    grid: {
                      drawOnChartArea: false,
                      drawBorder: false,
                      display: false
                    }
                  },
                  x: {
                    grace: 1,
                    ticks: { max: maxValue, display: false },
                    grid: {
                      drawOnChartArea: false,
                      drawBorder: false,
                      display: false
                    }
                  }
                },
                plugins: {
                  datalabels: { anchor: "end", align: "end", offset: 6 },
                  legend: { display: false },
                  tooltip: { enabled: false }
                },
                layout: {
                  padding: {
                    right: 20
                  }
                }
              }
            }
          };
        }
      );
    }
  }
};
</script>

<style scoped>
.document-omt-tab-container {
  position: relative;
  flex-grow: 1;
  width: 100%;
  height: 100%;
}

.form-result {
  background: white;
  padding: 12px;
  padding-right: 8px;
  border-radius: 10px;
}

.form-result:not(:first-of-type) {
  margin-top: 16px;
}

.form-name {
  font-size: 1rem;
  margin-bottom: 0px;
}

.form-subtitle {
  display: block;
  color: #aaa;
  font-size: 14px;
  margin-top: 4px;
}

.line-chart {
  height: 100px;
  margin-top: 12px;
  margin-bottom: -5px;
}

.bar-chart-wrapper {
  max-height: 200px;
  overflow: hidden auto;
}

.omt-results-table >>> tbody tr:nth-of-type(odd) {
  background-color: #eff4fb;
}
.omt-results-table >>> th,
.omt-results-table >>> td {
  font-size: 14px;
}
</style>
